import { StorageOptionsStatus } from '../../Constant'
import { MAX_CACHE_TIME } from '../../Constant/storage'
import { executeWithRetry } from '../networking/networkHelpers'
import { indexedDBHelper } from '../storage/indexedDB'

export const storeInCache = async (
  key: string,
  item: any,
  storageStatuses: StorageOptionsStatus,
  ttl: number = MAX_CACHE_TIME
): Promise<void> => {
  const { indexedDB, local, session } = storageStatuses
  if (!indexedDB && !local && !session) return
  //Append last time updated
  const appendedItem = {
    data: item,
    lastUpdated: new Date().toISOString(),
    ttl,
  }
  if (indexedDB) {
    try {
      await executeWithRetry(async () => {
        await indexedDBHelper.storeItem(key, appendedItem)
      }, `store in indexedDB: ${key}`)
      return
    } catch (error) {
      console.error(`IndexedDB storage failed for key "${key}":`, error)
      // Proceed to fallback options
    }
  }
  if (local) {
    try {
      await executeWithRetry(async () => {
        localStorage.setItem(key, JSON.stringify(appendedItem))
      }, `store in local storage: ${key}`)
      return
    } catch (e: any) {
      if (e?.name === 'QuotaExceededError') {
        console.error('LocalStorage limit exceeded!')
      } else {
        console.error('An unexpected error occurred:', e)
      }
    }
  }
  if (session) {
    try {
      await executeWithRetry(async () => {
        sessionStorage.setItem(key, JSON.stringify(appendedItem))
      }, `store in session storage: ${key}`)
      return
    } catch (e: any) {
      if (e?.name === 'QuotaExceededError') {
        console.error('SessionStorage limit exceeded!')
      } else {
        console.error('An unexpected error occurred:', e)
      }
    }
  }
}

export const retrieveFromCache = async (
  key: string,
  storageStatuses: StorageOptionsStatus,
  customTTL?: number
): Promise<{ data: any; }> => {
  const { indexedDB, local, session } = storageStatuses
  if (!indexedDB && !local && !session) return { data: null }

  const validateCacheData = (data: any): { data: any; } => {
    if (
      !data ||
      typeof data !== 'object' ||
      data.data === undefined ||
      !data.lastUpdated
    ) {
      return { data: null }
    }

    const ttlToUse = customTTL ?? data.ttl
    const isExpired = checkExpiredCache(data.lastUpdated, ttlToUse)
    if (isExpired) {
      deleteFromCache(key, storageStatuses)
      return { data: null }
    }
    return { data: data.data }
  }

  if (indexedDB) {
    try {
      const value = await executeWithRetry(async () => {
        return await indexedDBHelper.retrieveItem(key)
      }, `retrieve from indexedDB: ${key}`)
      return validateCacheData(value)
    } catch (error) {
      console.error(`IndexedDB retrieval failed for key "${key}":`, error)
    }
  }
  if (local) {
    try {
      const value = await executeWithRetry(async () => {
        const localItem = localStorage.getItem(key)
        return localItem ? JSON.parse(localItem) : null
      }, `retrieve from local storage: ${key}`)
      return validateCacheData(value)
    } catch (error) {
      console.error(`LocalStorage retrieval failed for key "${key}":`, error)
    }
  }

  if (session) {
    try {
      const value = await executeWithRetry(async () => {
        const sessionItem = sessionStorage.getItem(key)
        if (sessionItem) {
          try {
            const item = JSON.parse(sessionItem)
            return item
          } catch (parseError) {
            console.error(
              `Failed to parse cached session data for key "${key}":`,
              parseError
            )
            sessionStorage.removeItem(key)
            return null
          }
        }
      }, `retrieve from session storage: ${key}`)
      return validateCacheData(value)
    } catch (error) {
      console.error(`SessionStorage retrieval failed for key "${key}":`, error)
    }
  }

  return { data: null }
}

export const deleteFromCache = async (
  key: string,
  storageStatuses: StorageOptionsStatus
) => {
  const { indexedDB, local, session } = storageStatuses
  if (indexedDB) {
    try {
      await indexedDBHelper.deleteItem(key)
    } catch (error) {
      console.error(`IndexedDB deletion failed for key "${key}":`, error)
    }
  }
  if (local) {
    try {
      localStorage.removeItem(key)
    } catch (error) {
      console.error(`LocalStorage deletion failed for key "${key}":`, error)
    }
  }
  if (session) {
    try {
      sessionStorage.removeItem(key)
    } catch (error) {
      console.error(`SessionStorage deletion failed for key "${key}":`, error)
    }
  }
}

export const checkExpiredCache = (lastUpdated: string, ttl?: number) => {
  try {
    const lastUpdatedDate = new Date(lastUpdated).getTime()
    if (isNaN(lastUpdatedDate)) {
      // Invalid date, consider expired to trigger refresh
      return true
    }
    const now = new Date().getTime()
    const timeSinceUpdate = now - lastUpdatedDate
    return timeSinceUpdate > (ttl ?? MAX_CACHE_TIME)
  } catch (error) {
    console.error('Error checking cache expiration:', error)
    return true
  }
}
